(function () {
  'use strict';

  if (customElements.get('cuero-kinetic-hero')) return;

  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
  // Longest wait for a slide's shoe image to be ready before animating anyway.
  const PREPARE_TIMEOUT = 1200;

  const nextFrame = () => new Promise((resolve) => window.requestAnimationFrame(() => resolve()));
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  function decodeImage(image) {
    if (!image) return Promise.resolve();
    if (image.loading === 'lazy') image.loading = 'eager';
    if (typeof image.decode !== 'function') return Promise.resolve();
    return image.decode().catch(() => {});
  }

  // The value an element is showing right now, including any running animation.
  function readState(element) {
    if (!element) return { transform: 'none', opacity: 1 };
    const style = window.getComputedStyle(element);
    return { transform: style.transform || 'none', opacity: parseFloat(style.opacity) };
  }

  class CueroKineticHero extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this.eventAbortController = new AbortController();
      const signal = this.eventAbortController.signal;

      this.backgrounds = Array.from(this.querySelectorAll('[data-ckh-background]'));
      this.shoes = Array.from(this.querySelectorAll('[data-ckh-shoe]'));
      this.copies = Array.from(this.querySelectorAll('[data-ckh-copy]'));
      this.dots = Array.from(this.querySelectorAll('[data-ckh-dot]'));
      this.currentLabel = this.querySelector('[data-ckh-current]');
      this.progress = this.querySelector('[data-ckh-progress]');
      this.autoplayButton = this.querySelector('[data-ckh-autoplay]');
      this.previousButton = this.querySelector('[data-ckh-previous]');
      this.nextButton = this.querySelector('[data-ckh-next]');
      this.scrollPrompt = this.querySelector('[data-ckh-scroll]');

      // Everything that moves for one slide. `state` is hidden, entering, shown or exiting.
      this.slides = this.copies.map((copy, index) => {
        const shoe = this.shoes[index] || null;
        return {
          index,
          background: this.backgrounds[index] || null,
          shoe,
          shoeMotion: shoe ? shoe.querySelector('.ckh__shoe-motion') : null,
          image: shoe ? shoe.querySelector('img') : null,
          copy,
          copyMotion: copy.querySelector('.ckh__copy-motion'),
          heading: copy.querySelector('.ckh__heading > span'),
          state: 'hidden',
          animations: [],
          token: 0,
          ready: null,
          isReady: false
        };
      });

      this.slideCount = this.slides.length;
      this.current = Math.max(0, this.copies.findIndex((copy) => copy.classList.contains('is-active')));
      this.pending = null;
      this.waitingForSlide = false;
      this.generation = 0;
      this.busy = false;
      this.progressionMode = this.dataset.progressionMode || 'auto';
      this.scrollDriven = this.progressionMode === 'scroll' && this.slideCount > 1;
      this.duration = this.numberFromData('transitionDuration', 900);
      this.autoplayDelay = this.numberFromData('autoplayDelay', 5000);
      this.autoplayEnabled = !this.scrollDriven && this.dataset.autoplay === 'true' && this.slideCount > 1;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.canAnimate = typeof Element.prototype.animate === 'function' && !this.reducedMotion;
      this.progressAnimation = null;
      this.userPaused = false;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.visibilityPaused = document.hidden;
      this.offscreenPaused = false;
      this.editorPaused = false;
      this.pointerStart = null;
      this.scrollTicking = false;
      this.scrollFrame = 0;
      this.pendingScrollTarget = null;
      this.pendingScrollTimer = 0;
      this.warmTimer = 0;
      this.intersectionObserver = null;

      this.onPrevious = () => this.step(-1);
      this.onNext = () => this.step(1);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.handleBlockDeselect.bind(this);
      this.onHeaderScroll = this.handleHeaderScroll.bind(this);
      this.onScrollProgress = this.queueScrollProgressUpdate.bind(this);
      this.onMouseEnter = () => {
        this.hoverPaused = true;
        this.syncProgressPause();
      };
      this.onMouseLeave = () => {
        this.hoverPaused = false;
        this.syncProgressPause();
      };
      this.onFocusIn = () => {
        this.focusPaused = true;
        this.syncProgressPause();
      };
      this.onFocusOut = (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.syncProgressPause();
      };

      if (this.previousButton) this.previousButton.addEventListener('click', this.onPrevious, { signal });
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext, { signal });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const target = Number(dot.dataset.ckhDot);
          this.requestSlide(target, target >= this.current ? 1 : -1);
        }, { signal });
      });

      if (this.autoplayButton) {
        this.autoplayButton.addEventListener('click', () => this.toggleAutoplay(), { signal });
      }

      if (this.scrollPrompt) {
        this.scrollPrompt.addEventListener('click', (event) => this.scrollToNextSection(event), { signal });
      }

      this.addEventListener('keydown', this.onKeydown, { signal });
      this.addEventListener('pointerdown', this.onPointerDown, { passive: true, signal });
      this.addEventListener('pointerup', this.onPointerUp, { passive: true, signal });
      this.addEventListener('pointercancel', this.onPointerUp, { passive: true, signal });
      document.addEventListener('visibilitychange', this.onVisibilityChange, { signal });
      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect, { signal });

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.onMouseEnter, { signal });
        this.addEventListener('mouseleave', this.onMouseLeave, { signal });
        this.addEventListener('focusin', this.onFocusIn, { signal });
        this.addEventListener('focusout', this.onFocusOut, { signal });
      }

      if (this.scrollDriven) {
        window.addEventListener('scroll', this.onScrollProgress, { passive: true, signal });
        window.addEventListener('resize', this.onScrollProgress, { passive: true, signal });
      }

      // Autoplay only counts down while the hero is actually on screen.
      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.offscreenPaused = !entries[entries.length - 1].isIntersecting;
          this.syncProgressPause();
        });
        this.intersectionObserver.observe(this);
      }

      this.applyLightHeader();
      this.showOnly(this.current);
      this.startProgress();
      if (this.scrollDriven) this.queueScrollProgressUpdate();
      this.scheduleWarmup();
    }

    disconnectedCallback() {
      if (!this._initialized) return;
      this.generation += 1;
      this.pending = null;
      this.slides.forEach((slide) => this.cancelAnimations(slide));
      this.stopProgress();
      this.eventAbortController?.abort();
      this.eventAbortController = null;
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
      window.cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.warmTimer);
      window.clearTimeout(this.pendingScrollTimer);
      this.scrollFrame = 0;
      this.scrollTicking = false;

      if (this.previousButton) this.previousButton.removeEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('pointercancel', this.onPointerUp);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.removeEventListener('scroll', this.onHeaderScroll);
      window.removeEventListener('scroll', this.onScrollProgress);
      window.removeEventListener('resize', this.onScrollProgress);

      if (this.pauseOnHover) {
        this.removeEventListener('mouseenter', this.onMouseEnter);
        this.removeEventListener('mouseleave', this.onMouseLeave);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);
      }

      if (this.headerWrapper && !this.headerWasLight) {
        this.headerWrapper.classList.remove('is-light');
      }
      this._initialized = false;
    }

    numberFromData(key, fallback) {
      const value = Number(this.dataset[key]);
      return Number.isFinite(value) ? value : fallback;
    }

    clampIndex(index) {
      if (!this.slideCount) return 0;
      return Math.min(this.slideCount - 1, Math.max(0, index));
    }

    normalizeIndex(index) {
      if (!this.slideCount) return 0;
      return ((index % this.slideCount) + this.slideCount) % this.slideCount;
    }

    // Arrows, swipes and arrow keys: every press moves exactly one slide.
    step(delta) {
      if (this.scrollDriven) {
        const base = this.pendingScrollTarget !== null ? this.pendingScrollTarget : this.current;
        this.scrollToSlide(base + delta);
        return;
      }
      const base = this.pending ? this.pending.index : this.current;
      this.goTo(base + delta, delta);
    }

    requestSlide(requestedIndex, direction) {
      if (this.scrollDriven) {
        this.scrollToSlide(requestedIndex);
        return;
      }
      this.goTo(requestedIndex, direction);
    }

    goTo(requestedIndex, direction, options) {
      const settings = options || {};
      if (!this.slideCount) return;
      const index = this.normalizeIndex(requestedIndex);

      if (settings.instant) {
        this.generation += 1;
        this.pending = null;
        this.waitingForSlide = false;
        this.showOnly(index);
        this.startProgress();
        return;
      }

      if (index === this.current) {
        this.pending = null;
        return;
      }

      this.pending = { index, direction: direction ? Math.sign(direction) : index > this.current ? 1 : -1 };
      this.processPending();
    }

    // Starts the requested transition straight away. Only right after page load, when
    // a shoe may not be downloaded yet, does it wait (briefly) for that shoe first.
    async processPending() {
      if (this.waitingForSlide) return;

      while (this.pending && this.isConnected) {
        const { index, direction } = this.pending;
        if (index === this.current) {
          this.pending = null;
          return;
        }

        const slide = this.slides[index];
        if (!slide.isReady) {
          const generation = this.generation;
          this.waitingForSlide = true;
          await Promise.race([this.warmSlide(index), wait(PREPARE_TIMEOUT)]);
          this.waitingForSlide = false;
          if (generation !== this.generation || !this.isConnected) return;
          slide.isReady = true;
          continue;
        }

        this.pending = null;
        this.startTransition(index, direction);
      }
    }

    startTransition(toIndex, direction) {
      const from = this.slides[this.current];
      const to = this.slides[toIndex];
      const sign = direction >= 0 ? 1 : -1;

      this.stopProgress();
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.current = toIndex;
      this.applySlideColors(toIndex);
      this.updateIndicators(toIndex);

      if (!this.canAnimate || !from || !to) {
        this.showOnly(toIndex);
        this.startProgress();
        return;
      }

      this.exitSlide(from, sign);
      this.enterSlide(to, sign);
    }

    // The outgoing slide leaves from exactly where it is right now, even mid-animation,
    // so a quick second click never makes anything jump.
    exitSlide(slide, sign) {
      const duration = this.duration;
      const wasSettled = slide.state === 'shown';
      const background = readState(slide.background);
      const shoe = readState(slide.shoeMotion);
      const copy = readState(slide.copyMotion);
      const token = ++slide.token;

      // The heading keeps whatever it was doing; it fades out with the rest of the copy.
      this.cancelAnimations(slide, slide.heading);
      slide.state = 'exiting';
      this.setLayer(slide, 'exiting');
      slide.copy.setAttribute('aria-hidden', 'true');
      slide.copy.style.pointerEvents = 'none';
      this.setCopyTabStops(slide.copy, false);

      const animations = [];
      if (slide.background) {
        animations.push(slide.background.animate(
          [{ opacity: background.opacity }, { opacity: 0 }],
          { duration, easing: EASING, fill: 'forwards' }
        ));
      }

      if (slide.shoeMotion) {
        const end = { opacity: 0, transform: `translate3d(${-42 * sign}%, 5%, 0) scale(0.54)` };
        const keyframes = wasSettled
          ? [
              { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
              { opacity: 1, transform: `translate3d(${-12 * sign}%, 1%, 0) scale(0.86)`, offset: 0.48 },
              end
            ]
          : [{ opacity: shoe.opacity, transform: shoe.transform }, end];
        animations.push(slide.shoeMotion.animate(keyframes, { duration, easing: EASING, fill: 'forwards' }));
      }

      if (slide.copyMotion) {
        animations.push(slide.copyMotion.animate(
          [
            { opacity: copy.opacity, transform: copy.transform },
            { opacity: 0, transform: `translate3d(${-18 * sign}px, -18px, 0)` }
          ],
          { duration: Math.round(duration * 0.48), easing: EASING, fill: 'forwards' }
        ));
      }

      slide.animations = slide.animations.concat(animations);
      this.whenFinished(animations, () => {
        if (token !== slide.token) return;
        this.hideSlide(slide);
        this.checkSettled();
      });
    }

    enterSlide(slide, sign) {
      const duration = this.duration;
      const comingBack = slide.state === 'exiting';
      const background = readState(slide.background);
      const shoe = readState(slide.shoeMotion);
      const copy = readState(slide.copyMotion);
      const heading = readState(slide.heading);
      const token = ++slide.token;

      this.cancelAnimations(slide);
      slide.state = 'entering';
      this.setLayer(slide, 'entering');
      if (slide.background) slide.background.classList.add('is-active');
      if (slide.shoe) slide.shoe.classList.add('is-active');
      slide.copy.classList.add('is-active');
      slide.copy.style.removeProperty('pointer-events');
      slide.copy.setAttribute('aria-hidden', 'false');
      this.setCopyTabStops(slide.copy, true);

      const animations = [];
      if (slide.background) {
        animations.push(slide.background.animate(
          [{ opacity: comingBack ? background.opacity : 0 }, { opacity: 1 }],
          { duration, easing: EASING, fill: 'both' }
        ));
      }

      if (slide.shoeMotion) {
        const end = { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' };
        const keyframes = comingBack
          ? [{ opacity: shoe.opacity, transform: shoe.transform }, end]
          : [
              { opacity: 0, transform: `translate3d(${44 * sign}%, 5%, 0) scale(0.3)` },
              { opacity: 1, transform: `translate3d(${31 * sign}%, 3%, 0) scale(0.46)`, offset: 0.2 },
              end
            ];
        animations.push(slide.shoeMotion.animate(keyframes, { duration, easing: EASING, fill: 'both' }));
      }

      if (slide.copyMotion) {
        animations.push(slide.copyMotion.animate(
          [
            comingBack
              ? { opacity: copy.opacity, transform: copy.transform }
              : { opacity: 0, transform: `translate3d(${18 * sign}px, 28px, 0)` },
            { opacity: 1, transform: 'translate3d(0, 0, 0)' }
          ],
          {
            duration: Math.round(duration * 0.7),
            delay: comingBack ? 0 : Math.round(duration * 0.22),
            easing: EASING,
            fill: 'both'
          }
        ));
      }

      if (slide.heading) {
        animations.push(slide.heading.animate(
          [
            { transform: comingBack ? heading.transform : 'translate3d(0, 108%, 0) rotate(1.5deg)' },
            { transform: 'translate3d(0, 0, 0) rotate(0)' }
          ],
          {
            duration: Math.round(duration * 0.72),
            delay: comingBack ? 0 : Math.round(duration * 0.16),
            easing: EASING,
            fill: 'both'
          }
        ));
      }

      slide.animations = animations;
      this.whenFinished(animations, () => {
        if (token !== slide.token) return;
        slide.state = 'shown';
        this.cancelAnimations(slide);
        this.checkSettled();
      });
    }

    whenFinished(animations, callback) {
      Promise.all(animations.map((animation) => animation.finished.catch(() => {}))).then(() => {
        if (this.isConnected) callback();
      });
    }

    // Once nothing is moving any more, tidy up and restart the autoplay timer.
    checkSettled() {
      const settled = this.slides.every((slide) => (
        slide.index === this.current ? slide.state === 'shown' : slide.state === 'hidden'
      ));
      if (!settled || this.pending) return;
      this.slides.forEach((slide) => this.setLayer(slide, null));
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.startProgress();
    }

    hideSlide(slide) {
      this.cancelAnimations(slide);
      slide.state = 'hidden';
      this.setLayer(slide, null);
      if (slide.background) slide.background.classList.remove('is-active');
      if (slide.shoe) slide.shoe.classList.remove('is-active');
      slide.copy.classList.remove('is-active');
      slide.copy.style.removeProperty('pointer-events');
      slide.copy.setAttribute('aria-hidden', 'true');
      this.setCopyTabStops(slide.copy, false);
    }

    // Shows one slide with no animation (first load, theme editor, reduced motion).
    showOnly(index) {
      this.current = this.normalizeIndex(index);
      this.slides.forEach((slide) => {
        slide.token += 1;
        if (slide.index === this.current) {
          this.cancelAnimations(slide);
          slide.state = 'shown';
          this.setLayer(slide, null);
          if (slide.background) slide.background.classList.add('is-active');
          if (slide.shoe) slide.shoe.classList.add('is-active');
          slide.copy.classList.add('is-active');
          slide.copy.style.removeProperty('pointer-events');
          slide.copy.setAttribute('aria-hidden', 'false');
          this.setCopyTabStops(slide.copy, true);
        } else {
          this.hideSlide(slide);
        }
      });
      this.applySlideColors(this.current);
      this.updateIndicators(this.current);
      this.busy = false;
      this.removeAttribute('aria-busy');
      if (this.scrollDriven) this.updateScrollProgressIndicator();
    }

    // Outgoing shoes sit above the incoming one (as in the original design);
    // the incoming background fades in above the outgoing ones.
    setLayer(slide, role) {
      const layers = role === 'entering'
        ? { background: '2', shoe: '2', copy: '2' }
        : role === 'exiting'
          ? { background: '1', shoe: '3', copy: '3' }
          : null;
      [['background', slide.background], ['shoe', slide.shoe], ['copy', slide.copy]].forEach(([key, element]) => {
        if (!element) return;
        if (layers) element.style.zIndex = layers[key];
        else element.style.removeProperty('z-index');
      });
    }

    cancelAnimations(slide, keep) {
      const kept = [];
      slide.animations.forEach((animation) => {
        if (keep && animation.effect && animation.effect.target === keep) kept.push(animation);
        else animation.cancel();
      });
      slide.animations = kept;
    }

    updateIndicators(index) {
      this.dots.forEach((dot, itemIndex) => {
        const isActive = itemIndex === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });

      if (this.currentLabel) {
        this.currentLabel.textContent = String(index + 1).padStart(2, '0');
      }
    }

    // After the page has loaded, every slide is decoded and painted once and then kept
    // rendered but invisible (see the is-warm styles), so no click ever waits for it.
    scheduleWarmup() {
      if (this.slideCount < 2) return;
      const start = () => {
        window.clearTimeout(this.warmTimer);
        this.warmTimer = window.setTimeout(() => this.warmAll(), 250);
      };

      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true, signal: this.eventAbortController.signal });
    }

    async warmAll() {
      const order = [];
      for (let offset = 1; offset < this.slideCount; offset += 1) {
        // Next, previous, then further out.
        const next = this.normalizeIndex(this.current + Math.ceil(offset / 2) * (offset % 2 ? 1 : -1));
        if (!order.includes(next) && next !== this.current) order.push(next);
      }
      this.slides.forEach((slide) => {
        if (slide.index !== this.current && !order.includes(slide.index)) order.push(slide.index);
      });

      this.warmSlide(this.current);
      for (const index of order) {
        if (!this.isConnected) return;
        await this.warmSlide(index);
      }
    }

    warmSlide(index) {
      const slide = this.slides[index];
      if (!slide) return Promise.resolve();
      if (slide.ready) return slide.ready;

      if (slide.background) slide.background.classList.add('is-warm');
      if (slide.shoe) slide.shoe.classList.add('is-warm');

      // Two frames: the browser paints the newly revealed layers in between.
      slide.ready = Promise.all([decodeImage(slide.image), nextFrame().then(nextFrame)]).then(() => {
        slide.isReady = true;
      });
      return slide.ready;
    }

    setCopyTabStops(copy, isActive) {
      copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (isActive) {
          if (element.dataset.ckhOriginalTabindex !== undefined) {
            const original = element.dataset.ckhOriginalTabindex;
            if (original === '') element.removeAttribute('tabindex');
            else element.setAttribute('tabindex', original);
            delete element.dataset.ckhOriginalTabindex;
          } else if (element.getAttribute('tabindex') === '-1') {
            element.removeAttribute('tabindex');
          }
        } else {
          if (element.dataset.ckhOriginalTabindex === undefined) {
            element.dataset.ckhOriginalTabindex = element.getAttribute('tabindex') || '';
          }
          element.setAttribute('tabindex', '-1');
        }
      });
    }

    applySlideColors(index) {
      const copy = this.copies[index];
      if (!copy) return;
      const textColor = copy.dataset.ckhTextColor || '#f4efe6';
      const accentColor = copy.dataset.ckhAccentColor || '#c0a46b';
      this.style.setProperty('--ckh-control-color', textColor);
      this.style.setProperty('--ckh-accent-color', accentColor);
    }

    startProgress() {
      this.stopProgress();
      if (this.scrollDriven || !this.autoplayEnabled || this.reducedMotion || !this.progress || this.busy) return;

      const animation = this.progress.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: this.autoplayDelay, easing: 'linear', fill: 'forwards' }
      );
      this.progressAnimation = animation;

      // onfinish (unlike the one-off finished promise) fires again if a paused timer is replayed.
      animation.onfinish = () => {
        if (this.progressAnimation !== animation || this.shouldPause() || this.busy) return;
        this.progressAnimation = null;
        this.step(1);
      };

      this.syncProgressPause();
    }

    stopProgress() {
      if (this.progressAnimation) {
        this.progressAnimation.onfinish = null;
        this.progressAnimation.cancel();
        this.progressAnimation = null;
      }
      if (this.progress && !this.scrollDriven) this.progress.style.transform = 'scaleX(0)';
    }

    shouldPause() {
      return this.userPaused || this.hoverPaused || this.focusPaused || this.visibilityPaused || this.offscreenPaused || this.editorPaused;
    }

    syncProgressPause() {
      if (this.scrollDriven) return;
      if (!this.progressAnimation) {
        if (!this.shouldPause() && this.autoplayEnabled && !this.busy) this.startProgress();
        return;
      }
      if (this.shouldPause()) this.progressAnimation.pause();
      else this.progressAnimation.play();
    }

    toggleAutoplay() {
      if (this.scrollDriven) return;
      this.userPaused = !this.userPaused;
      if (this.autoplayButton) {
        this.autoplayButton.classList.toggle('is-paused', this.userPaused);
        this.autoplayButton.setAttribute('aria-pressed', String(this.userPaused));
        this.autoplayButton.setAttribute('aria-label', this.userPaused ? 'Play carousel' : 'Pause carousel');
      }
      this.syncProgressPause();
    }

    queueScrollProgressUpdate() {
      if (!this.scrollDriven || this.scrollTicking) return;
      this.scrollTicking = true;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.scrollTicking = false;
        if (!this.isConnected) return;
        this.updateScrollProgress();
      });
    }

    getScrollMetrics() {
      const rect = this.getBoundingClientRect();
      const viewportHeight = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
      const scrollableDistance = Math.max(1, rect.height - viewportHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / scrollableDistance));
      return { progress, rect, scrollableDistance };
    }

    updateScrollProgressIndicator(progress) {
      if (!this.progress) return;
      const nextProgress = Number.isFinite(progress) ? progress : this.getScrollMetrics().progress;
      const safeProgress = Math.min(1, Math.max(0, nextProgress));
      this.progress.style.transform = `scaleX(${safeProgress})`;
    }

    updateScrollProgress() {
      if (!this.scrollDriven) return;
      const metrics = this.getScrollMetrics();
      this.updateScrollProgressIndicator(metrics.progress);

      if (this.editorPaused) return;

      const index = this.clampIndex(Math.round(metrics.progress * (this.slideCount - 1)));
      if (index === this.pendingScrollTarget) this.pendingScrollTarget = null;
      const target = this.pending ? this.pending.index : this.current;
      if (index === target) return;

      this.goTo(index, index > target ? 1 : -1);
    }

    scrollToSlide(requestedIndex) {
      if (!this.scrollDriven || this.slideCount < 2) return;
      const parsedIndex = Number(requestedIndex);
      const targetIndex = this.clampIndex(Number.isFinite(parsedIndex) ? parsedIndex : this.current);
      const metrics = this.getScrollMetrics();
      const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const heroTop = currentY + metrics.rect.top;
      const targetProgress = targetIndex / (this.slideCount - 1);
      const targetY = heroTop + metrics.scrollableDistance * targetProgress;

      // Remember where a smooth scroll is heading, so a second click continues from there.
      this.pendingScrollTarget = targetIndex;
      window.clearTimeout(this.pendingScrollTimer);
      this.pendingScrollTimer = window.setTimeout(() => {
        this.pendingScrollTarget = null;
      }, 1500);

      window.scrollTo({
        top: targetY,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      }
    }

    handlePointerDown(event) {
      if (!event.isPrimary || event.target.closest('a, button')) return;
      this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    handlePointerUp(event) {
      if (!this.pointerStart || !event.isPrimary) return;
      const start = this.pointerStart;
      this.pointerStart = null;
      // pointercancel means the browser took the gesture over (e.g. the page scrolled).
      // Its coordinates are not a real end point, so it must never count as a swipe.
      if (event.type === 'pointercancel') return;

      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;
      this.step(deltaX < 0 ? 1 : -1);
    }

    handleVisibilityChange() {
      this.visibilityPaused = document.hidden;
      this.syncProgressPause();
    }

    handleBlockSelect(event) {
      if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
      const block = event.target.closest('[data-ckh-block-id]');
      if (!block) return;
      const index = Number(block.dataset.ckhCopy);
      if (!Number.isFinite(index)) return;
      this.editorPaused = true;
      this.goTo(index, 1, { instant: true });
      this.syncProgressPause();
    }

    handleBlockDeselect(event) {
      if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
      this.editorPaused = false;
      this.syncProgressPause();
      if (this.scrollDriven) this.queueScrollProgressUpdate();
    }

    scrollToNextSection(event) {
      if (this.scrollDriven && this.current < this.slideCount - 1) {
        event.preventDefault();
        this.scrollToSlide(this.current + 1);
        return;
      }
      const shopifySection = this.closest('.shopify-section');
      const nextSection = shopifySection && shopifySection.nextElementSibling;
      if (!nextSection) return;
      event.preventDefault();
      nextSection.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
    }

    applyLightHeader() {
      if (this.dataset.lightHeader !== 'true') return;
      this.headerWrapper = document.querySelector(
        '#HeaderWrapper, .cuero-header-shell, sticky-header, .header-wrapper'
      );
      if (!this.headerWrapper) return;
      this.headerWasLight = this.headerWrapper.classList.contains('is-light');
      window.addEventListener('scroll', this.onHeaderScroll, {
        passive: true,
        signal: this.eventAbortController.signal
      });
      this.handleHeaderScroll();
    }

    handleHeaderScroll() {
      if (!this.headerWrapper) return;
      if (window.scrollY > 250) this.headerWrapper.classList.remove('is-light');
      else this.headerWrapper.classList.add('is-light');
    }
  }

  customElements.define('cuero-kinetic-hero', CueroKineticHero);
})();
